import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { BACKGROUND, PEARL, PRIMARY, RED } from '../../../shared/Helpers/colors';
import { sessionTypeColors } from './constants';
import { DayProperties, FindColorProperties, FindColorReturnProperties } from './interfaces';

import { CalendarEventItem, isDiscretionaryEvent } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { SoapNotesStatuses } from '../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';

export const filterByCompleted = (event: CalendarEventItem): boolean =>
	!isDiscretionaryEvent(event) && event.isCompleted && event.reportStatus?.status === SoapNotesStatuses.submitted;

export const filterByDrafts = (event: CalendarEventItem): boolean =>
	!isDiscretionaryEvent(event) && event.reportStatus?.status === SoapNotesStatuses.draft;

export const sortByTime = ({ a, b }: { a: CalendarEventItem; b: CalendarEventItem }): number => {
	const c = parseWallClockTime(a.startTime).hour();
	const d = parseWallClockTime(b.startTime).hour();
	if (c > d) return 1;
	if (c < d) return -1;

	return 0;
};

export const findDateRange = (days: DayProperties[]) => {
	const start = days[0];
	const end = days[days.length - 1];

	const startDate = dayjs(new Date(start.year, start.month, start.day))
		.format('YYYY-MM-DD')
		.split('T')[0];
	const endDate = dayjs(new Date(end.year, end.month, end.day))
		.format('YYYY-MM-DD')
		.split('T')[0];

	return { startDate, endDate };
};

export const findColor = ({ item, index, screenWidth }: FindColorProperties): FindColorReturnProperties => {
	if (!item) {
		return { backgroundColor: BACKGROUND.BASE[100], color: PEARL.BASE[500] };
	}

	if (item.status !== 1) {
		return {
			backgroundColor: PRIMARY.LIGHT[32],
			color: BACKGROUND.PRIMARY[100],
		};
	}

	if (index % 7 === 0) {
		return { backgroundColor: RED.LIGHT[48], color: PEARL.BASE[500] };
	}

	if (screenWidth < 821) {
		return { backgroundColor: BACKGROUND.PRIMARY[100], color: PEARL.BASE[500] };
	}

	return { backgroundColor: BACKGROUND.BASE[100], color: PEARL.BASE[500] };
};

export const isPastTime = (date: Date, allowedAfterDate: Date) => date >= allowedAfterDate;

export const findDuration = (start: string, end: string) => {
	const startDate = parseWallClockTime(start);
	const endDate = parseWallClockTime(end);
	if (endDate.isBefore(startDate)) return 'Incorrect time range';

	const startHour = startDate.hour();
	const endHour = endDate.hour();
	const startMinute = startDate.minute();
	const endMinute = endDate.minute();

	let hours = endHour - startHour;
	let minutes = endMinute - startMinute;

	if (minutes < 0) {
		hours -= 1;
		minutes += 60;
	}

	if (hours === 0) {
		return `${minutes} minutes`;
	}

	if (hours === 1) {
		return `${hours} hour ${minutes > 0 ? `${minutes} minutes` : ''}`;
	}

	return `${hours} hours ${minutes > 0 ? `${minutes} minutes` : ''}`;
};

export const findEventColor = (sessionType: number) => sessionTypeColors.find((x) => x.type === sessionType)?.color;

export const findEventIconColor = (sessionType: number) =>
	sessionTypeColors.find((x) => x.type === sessionType)?.iconColor;

export const useShowBTsSessions = (id: string, userId: string) => {
	const [params] = useSearchParams();
	const includeBTEvents = params.get('includeBTEvents');
	if (!includeBTEvents) return 1;

	return includeBTEvents === 'true' && userId === id ? 0.5 : 1;
};
